/**
 * BatchProcessMobileRow — one batch process as a card on a phone
 * (TV1-2294 ticket 48).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FULL PARITY WITH `BatchProcessCard`, ENUMERATED. The desktop card carries
 * seven things; all seven land here, in both densities unless noted:
 *
 *   status dot (pulsing while running)  -> same dot, same `statusConfig` colour,
 *                                          same pulse class
 *   operation label                     -> the card title. Resolved through
 *                                          `resolveOperationLabel( … )`, which is
 *                                          both the SAFE accessor (see below) and
 *                                          the only one that translates the
 *                                          operation-run family's interpolated
 *                                          label parts
 *   error-count badge                   -> same red `IconX` badge, same count
 *   relative time                       -> a dimmed meta line (comfortable).
 *                                          COMPACT DROPS IT — see the density note
 *   summary                             -> `renderer.getSummary( … )`, same call,
 *                                          same resolved-reference arguments
 *   progress bar                        -> same `progressPercentage` selector,
 *                                          same `showProgressBar` condition.
 *                                          COMPACT renders it as a "62%" figure
 *   retry / cancel / archive            -> the action strip (comfortable) or
 *                                          `MobileRowActionsMenu` (compact)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE PLACE MOBILE IS BETTER THAN DESKTOP, AND IT IS A BUG FIX, NOT A REDESIGN.
 * `BatchProcessCard.module.css` hides the action strip behind
 * `.actions { opacity: 0 }` + `.card:hover .actions { opacity: 1 }`, with an
 * `.actionsAlwaysVisible` escape for errored rows. A touch screen HAS NO HOVER, so
 * on a phone the desktop card's "Cancelar" and "Archivar" are unreachable —
 * permanently invisible, not merely hard to find. The mobile card therefore shows
 * its actions unconditionally. That is the same class of translation ticket 42
 * made when a desktop hover `Tooltip` became a `title`, and ticket 32 made for the
 * linked-items affordance: a hover-only control has to become a visible one, or it
 * does not exist on touch.
 *
 * TAP TARGETS. The action glyphs stay 16px (the design system's table-action-icon
 * convention, which `BatchProcessCard` and `WarehouseMobileRow` both use), but each
 * sits in a 44x44 wrapper, so the TARGET meets the WCAG 2.5.5 / iOS HIG floor this
 * PR settled on without changing the visual weight of the strip.
 *
 * DENSITY: ONE ACTION SET, TWO LAYOUTS. `actions` is built ONCE — same gating,
 * same handlers, same labels, same testids — and rendered as the icon strip at
 * comfortable density and through `MobileRowActionsMenu` at compact. There is no
 * second list to keep in sync, so an action cannot exist in one density and not the
 * other (ticket 23's rule: compact sheds METADATA, never a capability).
 *
 * WHAT COMPACT SHEDS, AND WHAT IT REFUSES TO. It drops the relative time — pure
 * metadata. It does NOT drop progress: this is a list of RUNNING PROCESSES, so the
 * bar is the reason the list exists. It is rendered as a percentage figure in the
 * meta line rather than in `MobileCompactRow`'s `below` slot, because that slot is
 * documented as being for content that is itself an ACTION and never for what
 * compact is meant to shed — and a percentage costs ~34px on the line the row
 * already has instead of a whole second row.
 *
 * NO SELECTION, AND THEREFORE NO `.selected` FILL. `BatchProcessesList` has no
 * checkbox column and no bulk actions on desktop, so there is nothing to select and
 * `MobileSelectionBar` would never render. If selection is ever added here the
 * treatment is `light-dark(#ebf2f4, var(--mantine-color-dark-5))` +
 * `transition: background-color 180ms ease` + the card's NORMAL border and NO left
 * accent bar (owner override, TV1-2294) — and `.mobileCard:hover` below would then
 * need a `.mobileCard.selected:hover` twin, or the card would stop looking selected
 * exactly while the finger is on it.
 *
 * THE BODY TAP IS DESKTOP'S OWN. `BatchProcessCard` is `role="button"` with an
 * `onClick` that navigates to `/batch-processes/:id`, so "tap the body = open the
 * detail" is a rendering of existing behaviour, not a new affordance. The action
 * strip is a SIBLING of the tappable body (never a descendant), so a tap on an
 * action can never bubble into the navigation.
 *
 * OPERATION NAMES GO THROUGH `getRenderer`, NEVER A MAP INDEX. The readmodel
 * synthesises operation names at runtime as `Operation:<type>:<phase>`, which no
 * static map can cover; `getRenderer` falls back to `DefaultRenderer` /
 * `defaultOperationConfig`. Indexing `operationConfig` directly is what once
 * shipped a card titled with the literal string `undefined` (ticket 13).
 *
 * PURE, ON PURPOSE — no hooks. `t`, `locale` and `isCompact` all arrive as props
 * (the desktop card takes `t` the same way). The web-app's vitest runs in node mode
 * with no DOM, so a hook-free component can be invoked as a plain function and its
 * element tree walked — the oracle shape the ERP renderers and
 * `warehouseCapabilityGating` already rely on. A `useListDensity()` or
 * `useTranslation()` call in here would cost that.
 */

import {
  MobileCompactRow,
  MobileRowActionsMenu,
  type MobileRowAction
} from '@components/application/MobileList';
import { componentsTestIds } from '@components/testids';
import { ActionIcon, Badge, Paper, Progress, Text } from '@mantine/core';
import { IconArchive, IconRefresh, IconX } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { BatchProcessesReadmodelType } from '@bett3r-dev/teselly-domain';
import { statusConfig, resolveOperationLabel } from '../../../constants';
import { getRenderer, type ResolvedData } from '../../../detail/components/operationRenderers';
import { progressPercentage, isFinalizing } from '../../../shared/state/progressSelectors';
import { batchProcessesTestIds } from '../../../testids';
import { effectiveStatus as deriveEffectiveStatus } from './effectiveStatus';
import { CredentialRevokedNotice } from '@domains/integrations/channels/components/CredentialRevokedNotice/CredentialRevokedNotice';
import classes from './BatchProcessesMobile.module.css';

dayjs.extend( relativeTime );

export type BatchProcessMobileRowProps = {
  process: BatchProcessesReadmodelType;
  /** Batch Progress count (projection-tied). `null` falls back to the write side. */
  projectedCount?: number | null;
  /** Read once by the list container from `useListDensity`. */
  isCompact: boolean;
  /** `i18n.language`, read once by the list container so this stays hook-free. */
  locale: string;
  onOpen: () => void;
  onRetry?: ( processId: string ) => void;
  onArchive?: ( processId: string ) => void;
  onCancel?: ( processId: string ) => void;
  resolvedChannels?: ResolvedData['resolvedChannels'];
  resolvedWarehouses?: ResolvedData['resolvedWarehouses'];
  /**
   * TV1-2446 slice 6 — the batch's channel target whose credential is revoked, or `null`. While
   * set, Retry is rendered disabled and the Reconnect call to action pointing at it is shown,
   * at both densities.
   */
  revokedRetryTarget?: string | null;
  t: TFunction;
};

export const BatchProcessMobileRow = ({
  process,
  projectedCount = null,
  isCompact,
  locale,
  onOpen,
  onRetry,
  onArchive,
  onCancel,
  resolvedChannels,
  resolvedWarehouses,
  revokedRetryTarget = null,
  t
}: BatchProcessMobileRowProps ) => {
  const errorCount = process.erroredTargetIds?.length ?? 0;

  /* SHARED with `BatchProcessCard` — see `effectiveStatus.ts`. */
  const status = deriveEffectiveStatus( process );
  const statusVisual = statusConfig[status];

  const renderer = getRenderer( process.operationName );
  const label = resolveOperationLabel( process.operationName, t );
  const summary = renderer.getSummary(
    process.data,
    t,
    process,
    resolvedChannels,
    resolvedWarehouses
  );

  const isRunning = status === 'running';
  const isErrored = status === 'errored';
  /* Write side done, projections still catching up — the bar must stay up. */
  const finalizing = isFinalizing( process, projectedCount );
  const showProgressBar = isRunning || isErrored || finalizing;
  const progressValue = progressPercentage( process, projectedCount );

  const relativeTimeLabel = dayjs( process.updatedAt ).locale( locale )
.fromNow();

  /*
   * THE SINGLE SOURCE OF TRUTH FOR THIS CARD'S ACTIONS. Gating is
   * `BatchProcessCard`'s, unchanged: retry only on an errored run, cancel only on
   * a running one, archive on anything not running — and each is offered only when
   * the section actually passed the handler (history passes archive alone).
   *
   * Not memoized on purpose: every `onX` arrives as a stable `useCallback` from the
   * section, but a `useMemo` here would make the component hook-bearing, which is
   * exactly what the node-mode oracle cannot call.
   */
  const actions: MobileRowAction[] = [
    ...( onRetry && isErrored
      ? [{
        key: 'retry',
        label: t( 'batchProcesses_actions_retry' ),
        icon: (
          <IconRefresh
            size={16}
            style={{ transition: 'color 0.1s ease' }}
            className={classes.actionIcon}
          />
        ),
        testId: batchProcessesTestIds.listRowAction( 'retry', process.id ),
        disabled: revokedRetryTarget !== null,
        onClick: () => onRetry( process.id )
      }]
      : []),
    ...( onCancel && isRunning
      ? [{
        key: 'cancel',
        label: t( 'batchProcesses_actions_cancel' ),
        icon: (
          <IconX
            size={16}
            style={{ transition: 'color 0.1s ease' }}
            className={classes.actionIcon}
          />
        ),
        testId: batchProcessesTestIds.listRowAction( 'cancel', process.id ),
        onClick: () => onCancel( process.id )
      }]
      : []),
    ...( onArchive && !isRunning
      ? [{
        key: 'archive',
        label: t( 'batchProcesses_actions_archive' ),
        icon: (
          <IconArchive
            size={16}
            style={{ transition: 'color 0.1s ease' }}
            className={classes.actionIcon}
          />
        ),
        testId: batchProcessesTestIds.listRowAction( 'archive', process.id ),
        onClick: () => onArchive( process.id )
      }]
      : [])
  ];

  const statusDot = (
    <span
      className={`${ classes.statusDot }${ isRunning ? ` ${ classes.statusDotRunning }` : '' }`}
      style={{ backgroundColor: `var(--mantine-color-${ statusVisual.color }-6)` }}
    />
  );

  const errorBadge = errorCount > 0 ? (
    <Badge size="xs" color="red" variant="light" radius="xs" leftSection={<IconX size={10} />}>
      {errorCount}
    </Badge>
  ) : null;

  if ( isCompact ) {
    /*
     * COMPACT. One title line and one metadata line. NO `media` slot: a batch
     * process has no imagery, and the shell's fixed 36x36 box would spend 44px of
     * a ~176px content column on a decorative glyph. NO `checkbox` either — this
     * list has no selection.
     */
    return (
      <MobileCompactRow
        /* SAME value as the comfortable card: a spec targets "the card for
           process X" without knowing which density is active. */
        data-testid={batchProcessesTestIds.mobileRow( process.id )}
        onOpen={onOpen}
        title={label}
        titleTooltip={label}
        meta={
          <>
            {statusDot}
            {errorBadge}
            {showProgressBar && (
              <Text size="xs" fw={600} c="dimmed" className={classes.compactProgress}>
                {progressValue}%
              </Text>
            )}
            <Text size="xs" c="dimmed" lineClamp={1} title={summary}>
              {summary}
            </Text>
          </>
        }
        /*
          OMITTED, not passed empty, when there is no applicable action.
          `MobileRowActionsMenu` already returns `null` for an empty array, but the
          shell would still render its `.actions` wrapper and flex `gap` would
          leave a stray 8px beside the content column. Omitting the prop renders
          nothing at all — the same reasoning ticket 45 applied to `checkbox`.
        */
        actions={actions.length > 0 ? (
          <MobileRowActionsMenu
            actions={actions}
            ariaLabel={t( 'batchProcesses_mobile_row_more_actions', 'Más acciones' )}
            data-testid={componentsTestIds.mobileRowActionsMenu( process.id )}
          />
        ) : undefined}
      />
    );
  }

  return (
    <Paper
      data-testid={batchProcessesTestIds.mobileRow( process.id )}
      className={classes.mobileCard}
      withBorder
    >
      {/*
        The tappable region — desktop's own `onClick` (navigate to the detail),
        rendered as an explicit button role so it is keyboard/AT reachable. The
        action strip below is a SIBLING, so an action tap cannot bubble here.
      */}
      <div
        className={classes.mobileCardBody}
        onClick={onOpen}
        onKeyDown={( event ) => {
          if ( event.key === 'Enter' || event.key === ' ' ) {
            event.preventDefault();
            onOpen();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={label}
      >
        <div className={classes.mobileCardTitleRow}>
          {statusDot}
          <Text className={classes.mobileCardTitle} lineClamp={1} title={label}>
            {label}
          </Text>
          {errorBadge}
        </div>

        {/*
          Two lines maximum. The summary is a composed sentence ("Desde odoo",
          "A Depósito Central"), and a resolved-reference fallback can be a raw
          connector id, so it also carries `title`.
        */}
        <Text className={classes.mobileCardSummary} lineClamp={2} title={summary}>
          {summary}
        </Text>

        <Text className={classes.mobileCardTime}>{relativeTimeLabel}</Text>

        {showProgressBar && (
          <Progress
            className={classes.mobileCardProgress}
            value={progressValue}
            size={6}
            radius="xl"
            color={statusVisual.color}
            animated={isRunning || finalizing}
          />
        )}
      </div>

      {/* TV1-2446 slice 6 — why Retry is disabled. A sibling of the tappable body, like the strip. */}
      {onRetry && isErrored && revokedRetryTarget && (
        <CredentialRevokedNotice
          integrationId={revokedRetryTarget}
          message={t( 'batchProcesses_actions_retry_credential_revoked', 'No se puede reintentar: la conexión con el canal fue revocada.' )}
          reconnectLabel={t( 'batchProcesses_actions_reconnect_channel', 'Reconectar canal' )}
          testId={batchProcessesTestIds.listRetryCredentialRevokedNotice( process.id )}
        />
      )}

      {/*
        COMFORTABLE STRIP — ALWAYS VISIBLE (see the header: the desktop strip is
        hover-gated and therefore unreachable on touch). Same `actions` array the
        compact menu renders, so the two densities cannot diverge. A card with no
        applicable action renders no strip at all rather than an empty divider.
      */}
      {actions.length > 0 && (
        <div className={classes.mobileCardActions}>
          {actions.map(( action ) => (
            <span key={action.key} className={classes.actionButtonWrapper}>
              <ActionIcon
                variant="transparent"
                color="gray"
                size="sm"
                radius="xs"
                aria-label={action.label}
                /* Touch has no hover, so the desktop tooltip becomes a `title`. */
                title={action.label}
                data-testid={action.testId}
                disabled={action.disabled}
                onClick={( event ) => {
                  event.stopPropagation();
                  action.onClick();
                }}
              >
                {action.icon}
              </ActionIcon>
            </span>
          ))}
        </div>
      )}
    </Paper>
  );
};

export default BatchProcessMobileRow;
